import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Block from '@mui/icons-material/Block'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Chip, Dialog, DialogContent, DialogProps, DialogTitle, IconButton, Skeleton, Tab, Tabs, Typography } from '@mui/material'

import { GRBanner } from '../../../../components/GRBanner/GRBanner'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import ShareUrlButton from '../../../../components/ShareUrlButton/ShareUrlButton'
import { useErrorOnce } from '../../../../hooks/useError'
import languageService from '../../../../services/LanguageService'
import { colors } from '../../../../themes/theme'
import { useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import GameIcon from '../../../game/components/GameIcon/GameIcon'
import { GameLink } from '../../../game/components/GameLink/GameLink'
import { AppType } from '../../../game/types/Game'
import { buildBlueprintFamilyPath, buildEventCategoryPath, buildEventTypePath } from '../../../market-analytics/components/helpers/catalogueUtils'
import { useRouteModal } from '../../../route-modals/hooks/useRouteModal'
import { useLiveEventBlueprintName } from '../../hooks/liveEventsBlueprints'
import { useBlueprintFamilyForEvent } from '../../hooks/useBlueprintFamilyForEvent'
import { useEventDialogDuration } from '../../hooks/useEventDialogDuration'
import { EventDialogTab, useEventDialogTabs } from '../../hooks/useEventDialogTabs'
import { useLiveEventTagGroupWithEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { useLiveEventDialogAnalyticsEvents } from '../../hooks/useLiveEventsTrackerAnalyticsEvents'
import { useTrackedEvent } from '../../hooks/useTrackedEvent'
import { LiveEventTagGroup } from '../../types/LiveEventTagGroup'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent, TrackingEventByGame } from '../../types/TrackingEvents'
import { LiveEventEvolutionScope } from '../helpers/liveEventEvolutionTimelineHelpers'
import { AllEventsTab } from './AllEventsTab/AllEventsTab'
import { DescriptionTab } from './DescriptionTab/DescriptionTab'
import { EventDialogGameLine } from './EventDialogGameLine/EventDialogGameLine'
import { EventHierarchyPath, EventHierarchyPathSegment } from './EventHierarchyPath/EventHierarchyPath'
import { EvolutionTab } from './EvolutionTab/EvolutionTab'
import { InstancesTab } from './InstancesTab/InstancesTab'
import './LiveEventsEventDialog.scss'

/**
 * Displays a dialog with tabs for browsing single event details and all events related to a game
 */
type LiveEventsEventDialogProps = DialogProps & {
  data: LiveEventsEventDialogData
  children?: ReactNode
  onTrackedGameChange: (trackedGame: TrackedGame) => void
  /** Moves the dialog to another event, on its Description tab unless a tab is given. */
  onEventChange: (event: TrackingEvent, tab?: EventDialogTab) => void
  onTabChange: (tab: EventDialogTab) => void
  onScreenshotIndexChange: (screenshotIndex: number) => void
}

export type LiveEventsEventDialogData = {
  eventId?: string
  eventTypeId?: string
  trackedGame?: TrackedGame
  durationStart?: number
  durationEnd?: number
  tab?: EventDialogTab
  /** Scope the Blueprint tab opens on; its "First" versions view when omitted. */
  evolutionScope?: LiveEventEvolutionScope
  highlightedEventId?: string
  screenshotIndex?: number
  trackedEvents: TrackingEventByGame
  trackedGames?: TrackedGame[]
  /**
   * False while the game's data may not be requested yet (see `useEventGameDataAccess`). The tabs, which
   * request it, are not mounted until then. Defaults to true.
   */
  canRequestGameData?: boolean
}

const HEADER_ICON_SIZE = 78

export const LiveEventsEventDialog: FC<LiveEventsEventDialogProps> = ({
  open,
  onClose,
  onTrackedGameChange,
  onEventChange,
  onTabChange,
  onScreenshotIndexChange,
  data: {
    eventId,
    eventTypeId,
    trackedGame,
    durationStart,
    durationEnd,
    tab,
    evolutionScope,
    screenshotIndex,
    trackedEvents,
    trackedGames,
    canRequestGameData = true,
  },
  children,
}) => {
  const eventQuery = useTrackedEvent(eventId)
  useErrorOnce({ error: eventQuery.error, reportKey: eventId ?? '' })

  const { popAllModals } = useRouteModal()
  const tabOptions = useEventDialogTabs()
  const resolvedEventTypeId = !!eventId ? eventQuery.data?.event.typeId : eventTypeId
  const tagName = resolvedEventTypeId && languageService.getTranslation('tags', resolvedEventTypeId)
  const tagGroup = useLiveEventTagGroupWithEventTypeId(resolvedEventTypeId || '')
  const duration = useEventDialogDuration({ durations: eventQuery.data?.durations, durationStart, durationEnd })
  const blueprintId = eventQuery.data?.event.blueprintEvolutionData?.blueprintId
  const blueprintName = useLiveEventBlueprintName({ blueprintId, trackedGame })
  const isDiscontinued = useMemo(() => {
    return trackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === blueprintId)?.discontinued
  }, [blueprintId, trackedGame?.blueprints])
  const inactive = eventQuery.data?.event.active === false
  const showHierarchyRow = eventQuery.isLoading || Boolean(resolvedEventTypeId) || Boolean(blueprintName)
  const { t } = useTranslation()

  const dialogContentRef = useRef<HTMLElement>(null)
  useLiveEventDialogAnalyticsEvents(
    trackedGame?.game?.names['us'] || trackedGame?.game?.name,
    trackedGame?.game?.appId,
    eventId,
    tagName,
    resolvedEventTypeId,
    resolvedEventTypeId ? languageService.getTranslation('tags', resolvedEventTypeId) : '',
    tab
  )

  const switchTab = useCallback(
    (tab: EventDialogTab) => {
      onTabChange(tab)
    },
    [onTabChange]
  )
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const {
    familyId,
    familyName,
    canLink: canLinkFamily,
    isUncategorized,
    isFamilyNameLoading,
  } = useBlueprintFamilyForEvent({ trackedGame, gameLevelBlueprintId: blueprintId })

  // The hierarchy path in the header: always all four levels, in order, a
  // dash where the event cannot name one. Category, type and family lead to
  // their Live Event Analytics pages; the blueprint is what the dialog is
  // already about, so it stays plain.
  const hierarchyCells: EventHierarchyPathSegment[] = [
    {
      key: 'category',
      label: t('common:category'),
      value: tagGroup?.name || undefined,
      to: hasLiveEventsTrackerAccess && tagGroup?.id ? buildEventCategoryPath(tagGroup.id) : undefined,
    },
    {
      key: 'type',
      label: t('live-events:event_type_label'),
      value: tagName || undefined,
      to: hasLiveEventsTrackerAccess && resolvedEventTypeId && tagName ? buildEventTypePath(resolvedEventTypeId) : undefined,
    },
    familyId
      ? {
          key: 'blueprintFamily',
          label: t('live-events:market_level_blueprint'),
          // The family name arrives with the glossary; while it is in flight the
          // game-level name keeps the cell from flashing empty. Without the
          // entitlement the glossary is never fetched, so the cell stays a dash
          // rather than passing the game-level name off as the family's.
          value: familyName || (isFamilyNameLoading ? blueprintName : undefined),
          to: canLinkFamily ? buildBlueprintFamilyPath(familyId) : undefined,
        }
      : {
          key: 'blueprintFamily',
          label: t('live-events:market_level_blueprint'),
          // A blueprint no analyst has mapped yet: say so rather than leave a gap.
          value: isUncategorized ? t('market-analytics:granularity_blueprint') : undefined,
          isPlaceholder: isUncategorized,
          note: isUncategorized ? t('live-events:event_blueprint_not_categorized') : undefined,
        },
    {
      key: 'blueprint',
      label: t('live-events:blueprint'),
      value: blueprintName || undefined,
      adornment: isDiscontinued ? (
        <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ display: 'inline-flex', cursor: 'help' }}>
          <Block color="error" sx={{ fontSize: 16 }} />
        </GRTooltip>
      ) : undefined,
    },
  ]

  const handleEventChange = (event: TrackingEvent) => {
    switchTab(EventDialogTab.Description)
    onEventChange(event)
  }

  const handleOpenInstancesTab = (targetEventId: string, targetEvent?: TrackingEvent) => {
    if (targetEventId === eventId) {
      switchTab(EventDialogTab.Instances)
    } else if (targetEvent) {
      onEventChange(targetEvent, EventDialogTab.Instances)
    }
  }

  const handleEventHighlighted = (eventId: string) => {
    popAllModals()
  }

  useEffect(() => {
    if (tagGroup && tagGroup.id === LiveEventTagGroup.DEFINING_OCCURRENCES && tab !== EventDialogTab.Description) {
      switchTab(EventDialogTab.Description)
    }
  }, [tagGroup, tab, switchTab])

  // The Blueprint tab is a bounded panel that scrolls its own body under a
  // header that stays put. It has to be the only scroller while it is open, or
  // the reader gets two bars for one list. The other tabs are plain documents
  // and still scroll as a whole here.
  const tabScrollsItself = tab === EventDialogTab.LiveEventEvolution

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableRestoreFocus
      className="LiveEventsEventDialog"
      // The document tabs size the dialog to their content between 90vh and
      // 100vh. The self-scrolling Blueprint panel instead gets a fixed 90vh, so
      // the dialog does not resize as the timeline inside it changes length —
      // the panel keeps its height and scrolls less.
      PaperProps={{ sx: tabScrollsItself ? { height: '90vh', maxHeight: '100vh' } : { minHeight: '90vh', maxHeight: '100vh' } }}
    >
      {/* Header: a three-column grid. The game icon on the left sits in a
          fixed-size column (`HEADER_ICON_SIZE`), so its size does not depend on
          how tall the text column is or on the browser's grid sizing. The
          middle column stacks three lines like a page title — the game the
          event ran in, the event name, and the hierarchy the event belongs to
          as a labelled path (`EventHierarchyPath`). Share and close hold the
          top-right. No band under the header, so the tab row below is the only
          horizontal control strip. */}
      <DialogTitle
        component="div"
        sx={{
          px: '22px',
          py: '18px',
          display: 'grid',
          gridTemplateColumns: `${HEADER_ICON_SIZE}px 1fr auto`,
          gap: 2,
          alignItems: 'stretch',
          borderBottom: `1px solid ${colors.ruleSubtle}`,
        }}
      >
        {/* A fixed square, top-aligned in the row so the link's clickable area
            ends with the icon. `GameIcon` sizes itself from one CSS variable;
            it is set to the same pixel size here (and its proportional corner
            radius and type badge follow). Mobile games wrap the icon in a link,
            which is made to fill the box; PC/Console games render it bare. */}
        <Box
          sx={{
            width: HEADER_ICON_SIZE,
            height: HEADER_ICON_SIZE,
            alignSelf: 'start',
            '& > a': { display: 'block', width: '100%', height: '100%' },
            '& .GameIcon': { '--game-icon-size': `${HEADER_ICON_SIZE}px` },
          }}
        >
          {trackedGame && (
            <GameLink game={trackedGame.game} disabled={trackedGame.game.appType === AppType.PC_CONSOLE}>
              <GameIcon
                src={trackedGame.game.getIcon() as string}
                gameName={trackedGame.game.resolvedName}
                size="normal"
                appType={trackedGame.game.appType}
                gamePlatforms={trackedGame.game.platforms}
                discontinued={trackedGame.discontinued}
              />
            </GameLink>
          )}
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px', justifyContent: 'center' }}>
          {trackedGame && <EventDialogGameLine trackedGame={trackedGame} />}
          {eventQuery.isLoading ? (
            <Skeleton variant="text" width={280} sx={{ fontSize: '1.375rem' }} />
          ) : (
            <Typography
              variant="h2"
              component="h2"
              sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.2px', color: 'text.primary', overflowWrap: 'anywhere' }}
            >
              {eventQuery.data?.event.name || tagName || t('common:not_available_shorthand')}
              {inactive && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />}
            </Typography>
          )}
          {showHierarchyRow && <EventHierarchyPath segments={hierarchyCells} accentColor={tagGroup?.colorHex} isLoading={eventQuery.isLoading} />}
        </Box>
        {/* Top-aligned; the × is nudged down to sit on the Share pill's cap height. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <ShareUrlButton sx={{ fontSize: '0.8125rem', lineHeight: 1.3, px: '18px', py: '8px', minWidth: 0, '& .MuiButton-startIcon svg': { fontSize: 15 } }} />
          <IconButton
            aria-label={t('common:close')}
            onClick={(e) => onClose?.(e, 'escapeKeyDown')}
            size="small"
            sx={{ color: 'text.primary', mt: '5px', p: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      {tagGroup && tagGroup.id !== LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Tabs
          value={tab || EventDialogTab.Description}
          sx={{
            bgcolor: 'common.white',
            minHeight: 48,
            '& .MuiTabs-indicator': { height: 3 },
            '& .MuiTab-root': { fontSize: '0.8125rem', textTransform: 'uppercase', whiteSpace: 'nowrap', minHeight: 48 },
          }}
        >
          {tabOptions.map((tab) => {
            const disabled = [EventDialogTab.Description, EventDialogTab.Instances, EventDialogTab.LiveEventEvolution].includes(tab.id) && !eventId
            return (
              <Tab
                icon={tab.icon}
                iconPosition="start"
                key={tab.id}
                label={
                  disabled && tab.tooltip ? (
                    <GRTooltip content={tab.tooltip}>
                      <>{tab.label}</>
                    </GRTooltip>
                  ) : (
                    tab.label
                  )
                }
                onClick={() => !disabled && switchTab(tab.id)}
                value={tab.id}
                style={{ pointerEvents: 'auto' }}
                disabled={disabled}
                component="div"
              />
            )
          })}
        </Tabs>
      )}

      <DialogContent
        ref={dialogContentRef}
        // `scroll` rather than `auto` on the document tabs so the gutter is
        // always reserved and the content does not shift when a tab's length
        // crosses the overflow threshold. The Blueprint panel scrolls itself,
        // so this container must not scroll while that tab is open.
        style={{ overflowY: tabScrollsItself ? 'hidden' : 'scroll' }}
        sx={tabScrollsItself ? { display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}
      >
        {eventQuery.isError && !eventQuery.isFetching ? (
          <GRBanner severity="guidance">
            <Trans i18nKey="live-events:event_not_available" />
          </GRBanner>
        ) : !canRequestGameData ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <>
            {tab === EventDialogTab.Description && (
              <DescriptionTab
                currentTrackedGame={trackedGame}
                eventId={eventId}
                eventTypeId={resolvedEventTypeId}
                eventTypeName={tagName}
                screenshotIndex={screenshotIndex}
                duration={duration}
                onScreenshotIndexChange={onScreenshotIndexChange}
                onScrollParentTop={() => dialogContentRef && dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                onEventHighlighted={handleEventHighlighted}
              />
            )}
            {tab === EventDialogTab.Instances && (
              <InstancesTab
                eventId={eventId}
                eventTypeId={resolvedEventTypeId}
                currentTrackedGame={trackedGame}
                duration={duration}
                onEventHighlighted={handleEventHighlighted}
                trackedEvents={trackedEvents}
                trackedGames={trackedGames}
              />
            )}
            {tab === EventDialogTab.AllEvents && (
              <AllEventsTab
                currentTrackedGame={trackedGame}
                currentEventTypeId={resolvedEventTypeId}
                currentEventId={eventId}
                onEventSelect={handleEventChange}
              />
            )}
            {tab === EventDialogTab.LiveEventEvolution && (
              <EvolutionTab
                currentEventId={eventId}
                currentTrackedGame={trackedGame}
                onEventSelect={handleEventChange}
                onOpenInstancesTab={handleOpenInstancesTab}
                initialScope={evolutionScope}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
